/**
 * Ride Card Component
 * 
 * Reusable ride card component for displaying ride information in both
 * Atlas and database panels. Supports expandable accordion details,
 * selection, and validation/match status display.
 * 
 * @module RideCard
 */

import React, { useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Checkbox,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';

import type { RideSchema } from '../../../../types/simulation';
import type { UATRideEntry, UATRideDetailedEntry } from '../../../../services/fixedRouteUatClient';

import { Icon } from '@liftango/ui-icons';
/**
 * Validation status for upload operations
 */
export interface RideValidationStatus {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  userFound?: boolean;
  pickupLocationFound?: boolean;
  dropoffLocationFound?: boolean;
}

/**
 * Match status for download operations
 */
export interface RideMatchStatus {
  existsInAtlas: boolean;
  existingRideId?: string;
  isDuplicate: boolean;
  importPreview?: {
    willCreate: boolean;
    willUpdate: boolean;
  };
}

/**
 * Props for RideCard component
 */
export interface RideCardProps {
  /** Ride data - either RideSchema (Atlas) or UATRideEntry/UATRideDetailedEntry (database) */
  ride: RideSchema | UATRideEntry | UATRideDetailedEntry;
  /** Source of the ride ('atlas' or 'database') */
  source: 'atlas' | 'database';
  /** Whether the ride is selected */
  selected: boolean;
  /** Callback when selection changes */
  onSelect: (selected: boolean) => void;
  /** Whether the accordion is expanded (optional, controlled) */
  expanded?: boolean;
  /** Callback when expansion changes (optional, for controlled mode) */
  onExpand?: (expanded: boolean) => void;
  /** Validation status for upload operations (optional) */
  validationStatus?: RideValidationStatus;
  /** Match status for download operations (optional) */
  matchStatus?: RideMatchStatus;
  /** Timezone for displaying times (optional, defaults to UTC) */
  timezone?: string;
}

/**
 * RideCard Component
 * 
 * Displays ride information in a card format with expandable accordion details.
 * Supports both Atlas rides (RideSchema) and database rides (UATRideEntry).
 */
export const RideCard: React.FC<RideCardProps> = React.memo(({
  ride,
  source,
  selected,
  onSelect,
  expanded: controlledExpanded,
  onExpand,
  validationStatus,
  matchStatus,
  timezone,
}) => {
  const { t } = useTranslation('mapTransfer');
  // Internal state for expanded if not controlled
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;
  const handleExpandChange = (newExpanded: boolean) => {
    if (onExpand) {
      onExpand(newExpanded);
    } else {
      setInternalExpanded(newExpanded);
    }
  };

  // Extract ride information based on source
  const isAtlas = source === 'atlas';
  const rideSchema = isAtlas ? ride as RideSchema : null;
  const uatRide = !isAtlas ? ride as UATRideEntry | UATRideDetailedEntry : null;

  // Extract ride details
  let rideId: string;
  let pickupAddress: string | undefined;
  let dropoffAddress: string | undefined;
  let requestTime: string | undefined;
  let passengers: Array<{ type: string; number_of_passengers: number }> = [];
  let tripType: string | undefined;
  let userId: string | undefined;
  let userDisplayName: string | undefined;
  let rideStatus: string | undefined;

  if (isAtlas && rideSchema) {
    rideId = rideSchema.ride_id || rideSchema.external_reference_id || 'unknown';
    pickupAddress = rideSchema.from_address;
    dropoffAddress = rideSchema.to_address;
    requestTime = rideSchema.request_time;
    passengers = rideSchema.passengers || [];
    userId = rideSchema.user_id || rideSchema.external_user_reference_id;
  } else if (uatRide) {
    rideId = uatRide.id;
    rideStatus = uatRide.status; // Extract status for database rides
    // Extract from segments if available
    if ('segments' in uatRide && uatRide.segments && uatRide.segments.length > 0) {
      const pickupSegment = uatRide.segments.find(s => s.activity === 'pickup');
      const dropoffSegment = uatRide.segments.find(s => s.activity === 'dropoff');
      pickupAddress = pickupSegment?.address?.label || undefined;
      dropoffAddress = dropoffSegment?.address?.label || undefined;
    }
    requestTime = uatRide.departureTime || uatRide.time;
    passengers = uatRide.tripPassengers?.map((p, idx) => ({
      type: p.seatType === 'wheelchair' ? 'wheelchair' : 'standard',
      number_of_passengers: 1,
    })) || [];
    tripType = uatRide.tripType;
    userId = uatRide.owner?.id || uatRide.user?.id;
    userDisplayName = uatRide.owner 
      ? `${uatRide.owner.firstName} ${uatRide.owner.lastName}`.trim()
      : uatRide.user
      ? `${uatRide.user.firstName} ${uatRide.user.lastName}`.trim()
      : undefined;
  } else {
    rideId = 'unknown';
  }

  // Get status color and display text
  const getStatusInfo = (status: string | undefined) => {
    if (!status) return null;
    
    const statusLower = status.toLowerCase();
    
    if (statusLower === 'cancelled' || statusLower === 'canceled') {
      return {
        label: t('rides.card.statusCancelled'),
        color: 'error' as const,
        bgColor: 'rgba(211, 47, 47, 0.1)',
        textColor: '#d32f2f',
      };
    } else if (statusLower === 'expired') {
      return {
        label: t('rides.card.statusExpired'),
        color: 'warning' as const,
        bgColor: 'rgba(237, 108, 2, 0.1)',
        textColor: '#ed6c02',
      };
    } else if (statusLower === 'merged') {
      return {
        label: t('rides.card.statusMerged'),
        color: 'info' as const,
        bgColor: 'rgba(25, 118, 210, 0.1)',
        textColor: '#1976d2',
      };
    } else if (statusLower === 'requested' || statusLower === 'pending') {
      return {
        label: t('rides.card.statusRequested'),
        color: 'primary' as const,
        bgColor: 'rgba(25, 118, 210, 0.1)',
        textColor: '#1976d2',
      };
    } else if (statusLower === 'confirmed' || statusLower === 'assigned') {
      return {
        label: status.charAt(0).toUpperCase() + status.slice(1),
        color: 'success' as const,
        bgColor: 'rgba(46, 125, 50, 0.1)',
        textColor: '#2e7d32',
      };
    } else {
      // Default for unknown statuses
      return {
        label: status.charAt(0).toUpperCase() + status.slice(1),
        color: 'default' as const,
        bgColor: 'rgba(158, 158, 158, 0.1)',
        textColor: '#9e9e9e',
      };
    }
  };

  const statusInfo = getStatusInfo(rideStatus);

  // Calculate passenger counts
  const standardPassengers = passengers
    .filter(p => p.type === 'standard' || p.type === 'Standard')
    .reduce((sum, p) => sum + (p.number_of_passengers || 0), 0);
  const wheelchairPassengers = passengers
    .filter(p => p.type === 'wheelchair' || p.type === 'Wheelchair')
    .reduce((sum, p) => sum + (p.number_of_passengers || 0), 0);
  const totalPassengers = standardPassengers + wheelchairPassengers;

  // Format request time for display
  // Converts UTC times from API to network timezone for display
  // For Atlas rides (timezone-agnostic), extracts components directly from string
  const formatTime = (timeStr: string | undefined): string => {
    if (!timeStr) return t('rides.card.noTime');
    try {
      // For Atlas rides (timezone-agnostic), always extract components directly
      // This ensures the time is displayed exactly as stored, without timezone conversion
      if (isAtlas) {
        // Remove timezone info if present (Z, +HH:mm, -HH:mm)
        const cleanTimeString = timeStr.replace(/[Z+-]\d{2}:?\d{2}$|[Z]$/, '');
        
        // Extract date/time components directly from string (timezone-agnostic)
        const datetimeMatch = cleanTimeString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?/);
        
        if (datetimeMatch) {
          const year = datetimeMatch[1];
          const month = datetimeMatch[2];
          const day = datetimeMatch[3];
          const hour = parseInt(datetimeMatch[4], 10);
          const minute = datetimeMatch[5];
          const second = datetimeMatch[6];
          
          // Format as MM/DD/YYYY, HH:MM:SS AM/PM (US format)
          const monthNum = parseInt(month, 10);
          const dayNum = parseInt(day, 10);
          const hour12 = hour % 12 || 12;
          const ampm = hour >= 12 ? 'PM' : 'AM';
          
          return `${monthNum}/${dayNum}/${year}, ${hour12}:${minute}:${second} ${ampm}`;
        }
        
        // Fallback for Atlas rides if pattern doesn't match
        return timeStr;
      }
      
      // For database rides, parse and convert to network timezone
      // Parse the date string (handles UTC with Z, or timezone offsets)
      const date = new Date(timeStr);
      if (isNaN(date.getTime())) {
        return timeStr;
      }
      
      // If timezone is provided, convert to that timezone for display
      const displayTimezone = timezone || undefined;
      
      if (displayTimezone) {
        // Format in the network timezone
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: displayTimezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
        });
        return formatter.format(date);
      }
      
      // Fallback: use local timezone
      return date.toLocaleString();
    } catch {
      return timeStr;
    }
  };

  // Display ride ID (truncated if long)
  const displayRideId = rideId.length > 20 ? `${rideId.substring(0, 20)}...` : rideId;

  return (
    <Card
      sx={{
        mb: 1.5,
        border: selected ? 2 : 1,
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 2,
        boxShadow: selected ? 3 : 1,
        transition: 'all 0.2s ease-in-out',
        backgroundColor: statusInfo && !selected ? statusInfo.bgColor : 'background.paper',
        '&:hover': {
          boxShadow: selected ? 4 : 3,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Checkbox
            checked={selected}
            onChange={(e) => onSelect(e.target.checked)}
            size="small"
            sx={{ mt: 0.5 }}
            color="primary"
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Icon name="location-dot" size={20} style={{ color: '#ef7335' }} />
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontWeight: 600, 
                  flex: 1,
                  color: 'text.primary',
                  fontSize: '0.95rem',
                }}
              >
                {pickupAddress || t('rides.card.pickupLocation')}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: '0.875rem',
                }}
              >
                → {dropoffAddress || t('rides.card.dropoffLocation')}
              </Typography>
              {statusInfo && (
                <Chip
                  label={statusInfo.label}
                  size="small"
                  sx={{
                    backgroundColor: statusInfo.bgColor,
                    color: statusInfo.textColor,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: '24px',
                    border: `1px solid ${statusInfo.textColor}40`,
                  }}
                />
              )}
              {validationStatus && (
                <Chip
                  label={validationStatus.isValid ? t('rides.card.valid') : t('rides.card.invalid')}
                  color={validationStatus.isValid ? 'success' : 'error'}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: '0.75rem', height: '24px' }}
                />
              )}
              {matchStatus && matchStatus.isDuplicate && (
                <Chip
                  label={t('rides.card.duplicate')}
                  color="warning"
                  size="small"
                  sx={{ fontWeight: 600, fontSize: '0.75rem', height: '24px' }}
                />
              )}
            </Box>

            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5, 
                flexWrap: 'wrap', 
                mb: 1,
                p: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Icon name="clock" size={20} style={{ color: '#52565e' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {formatTime(requestTime)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Icon name="user" size={20} style={{ color: '#52565e' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {t('rides.card.passengers', { count: totalPassengers })}
                  {wheelchairPassengers > 0 && t('rides.card.wheelchairExtra', { value: wheelchairPassengers })}
                </Typography>
              </Box>
              {tripType && (
                <Chip
                  label={tripType === 'leaveAt' ? t('rides.card.tripTypeLeaveAt') : tripType === 'arriveBy' ? t('rides.card.tripTypeArriveBy') : tripType}
                  size="small"
                  variant="outlined"
                  sx={{ 
                    fontSize: '0.7rem',
                    height: '20px',
                    borderColor: 'primary.light',
                    color: 'primary.main',
                  }}
                />
              )}
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  fontFamily: 'monospace',
                  fontSize: '0.7rem',
                  opacity: 0.7,
                  ml: 'auto',
                }}
              >
                {t('rides.card.idLabel', { value: displayRideId })}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Accordion
          expanded={expanded}
          onChange={(_, isExpanded) => handleExpandChange(isExpanded)}
          sx={{
            mt: 1,
            boxShadow: 'none',
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: '8px 0' },
          }}
        >
          <AccordionSummary
            expandIcon={<Icon size={20} name="chevron-down" />}
            sx={{
              minHeight: 32,
              '&.Mui-expanded': { minHeight: 32 },
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {expanded ? t('rides.card.hideDetails') : t('rides.card.showDetails')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1, pb: 1 }}>
            <List dense>
              {pickupAddress && (
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={t('rides.card.pickupAddress')}
                    secondary={
                      <Fragment>
                        <Typography component="span" variant="body2" color="text.primary">
                          {pickupAddress}
                        </Typography>
                        {isAtlas && rideSchema && (
                          <Fragment>
                            <br />
                            <Typography component="span" variant="caption" color="text.secondary">
                              {t('rides.card.coordinates', { lat: rideSchema.from_latitude.toFixed(6), lng: rideSchema.from_longitude.toFixed(6) })}
                            </Typography>
                          </Fragment>
                        )}
                      </Fragment>
                    }
                  />
                </ListItem>
              )}
              {dropoffAddress && (
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={t('rides.card.dropoffAddress')}
                    secondary={
                      <Fragment>
                        <Typography component="span" variant="body2" color="text.primary">
                          {dropoffAddress}
                        </Typography>
                        {isAtlas && rideSchema && (
                          <Fragment>
                            <br />
                            <Typography component="span" variant="caption" color="text.secondary">
                              {t('rides.card.coordinates', { lat: rideSchema.to_latitude.toFixed(6), lng: rideSchema.to_longitude.toFixed(6) })}
                            </Typography>
                          </Fragment>
                        )}
                      </Fragment>
                    }
                  />
                </ListItem>
              )}
              {requestTime && (
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={t('rides.card.requestTime')}
                    secondary={formatTime(requestTime)}
                  />
                </ListItem>
              )}
              {userDisplayName && (
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={t('rides.card.user')}
                    secondary={userDisplayName}
                  />
                </ListItem>
              )}
              {userId && (
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={t('rides.card.userId')}
                    secondary={
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {userId}
                      </Typography>
                    }
                  />
                </ListItem>
              )}
              {passengers.length > 0 && (
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={t('rides.card.passengersTitle')}
                    secondary={
                      <Fragment>
                        {passengers.map((p, idx) => (
                          <Typography key={idx} component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {p.type}: {p.number_of_passengers}
                          </Typography>
                        ))}
                      </Fragment>
                    }
                  />
                </ListItem>
              )}
              {validationStatus && validationStatus.errors.length > 0 && (
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={t('rides.card.validationErrors')}
                    secondary={
                      <Fragment>
                        {validationStatus.errors.map((error, idx) => (
                          <Typography key={idx} component="span" variant="caption" color="error" sx={{ display: 'block' }}>
                            {error}
                          </Typography>
                        ))}
                      </Fragment>
                    }
                  />
                </ListItem>
              )}
              {validationStatus && validationStatus.warnings.length > 0 && (
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={t('rides.card.warnings')}
                    secondary={
                      <Fragment>
                        {validationStatus.warnings.map((warning, idx) => (
                          <Typography key={idx} component="span" variant="caption" color="warning.main" sx={{ display: 'block' }}>
                            {warning}
                          </Typography>
                        ))}
                      </Fragment>
                    }
                  />
                </ListItem>
              )}
            </List>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
});

RideCard.displayName = 'RideCard';

